/* BG lander video (unit 50c) — <bg-lp-c-video> in sections/bg-lp-c-video.liquid.
   Port of the live GemPages "jpl-video" / "m24-video" scripts: lazy src by width (<640 phone, 640-979 desktop,
   >=980 large), muted autoplay when >=45% visible and pause below 15% (unless the viewer paused it), poster tap =
   play with sound, video tap = pause/resume with a centre play button, left/right thirds = -/+10s, sound toggle
   (volume .72 from 760px, .18 below), seek bar (--fill), loop. Landscape instance restarts on first unmute
   (data-restart-on-unmute). Pauses when the tab is hidden. */
if (!customElements.get('bg-lp-c-video')) {
  customElements.define(
    'bg-lp-c-video',
    class BgLpCVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('.bg-lp-c-video__media');
        this.poster = this.querySelector('.bg-lp-c-video__poster');
        this.sound = this.querySelector('.bg-lp-c-video__sound');
        this.center = this.querySelector('.bg-lp-c-video__center');
        this.progress = this.querySelector('.bg-lp-c-video__progress');
        if (!this.video || !this.poster) return;

        this.manuallyPaused = false;
        this.seeking = false;
        this.pendingSeek = null;
        this.restartedOnUnmute = false;
        this.restartOnUnmute = this.dataset.restartOnUnmute === 'true';

        this.poster.addEventListener('click', () => this.play(true));
        this.video.addEventListener('click', () => {
          if (this.video.paused) {
            this.play(!this.video.muted);
          } else {
            this.manuallyPaused = true;
            this.pause(true);
          }
        });
        this.center?.addEventListener('click', (event) => {
          event.stopPropagation();
          this.play(!this.video.muted);
        });
        this.querySelector('.bg-lp-c-video__skip--back')?.addEventListener('click', (event) => {
          event.stopPropagation();
          this.seekBy(-10);
        });
        this.querySelector('.bg-lp-c-video__skip--forward')?.addEventListener('click', (event) => {
          event.stopPropagation();
          this.seekBy(10);
        });
        this.sound?.addEventListener('click', (event) => {
          event.stopPropagation();
          this.load();
          this.setQuietVolume();
          if (this.video.muted) this.restartOnFirstUnmute();
          this.video.muted = !this.video.muted;
          this.updateSound();
          if (this.video.paused) this.play(!this.video.muted);
        });

        this.video.addEventListener('play', () => this.showVideo());
        this.video.addEventListener('pause', () => {
          if (this.manuallyPaused && this.center && this.classList.contains('is-playing')) this.center.hidden = false;
        });
        this.video.addEventListener('volumechange', () => this.updateSound());
        this.video.addEventListener('timeupdate', () => this.updateProgress());
        this.video.addEventListener('loadedmetadata', () => {
          if (this.pendingSeek !== null) this.seekTo(this.pendingSeek);
          this.updateProgress();
        });

        if (this.progress) {
          this.progress.addEventListener('pointerdown', () => {
            this.seeking = true;
          });
          this.progress.addEventListener('input', () => {
            this.seeking = true;
            this.seekTo((parseFloat(this.progress.value) || 0) / 1000);
          });
          this.progress.addEventListener('change', () => {
            this.seekTo((parseFloat(this.progress.value) || 0) / 1000);
            this.seeking = false;
            this.updateProgress();
          });
        }

        // autoplay-in-view is skipped for prefers-reduced-motion (the poster stays until tapped)
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (reduceMotion && this.video.paused) return;
              if (entry.isIntersecting && entry.intersectionRatio >= 0.45 && this.video.paused && !this.manuallyPaused) {
                this.play(false);
              } else if (!entry.isIntersecting || entry.intersectionRatio < 0.15) {
                this.pause(false);
              }
            });
          },
          { threshold: [0, 0.15, 0.45, 0.75] }
        );
        this.observer.observe(this);

        this.onVisibility = () => {
          if (document.hidden) this.pause(false);
        };
        document.addEventListener('visibilitychange', this.onVisibility);
        this.updateSound();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        document.removeEventListener('visibilitychange', this.onVisibility);
      }

      source() {
        const mobile = this.dataset.srcMobile;
        const desktop = this.dataset.srcDesktop || mobile;
        const large = this.dataset.srcLarge || desktop;
        if (window.matchMedia('(min-width: 980px)').matches && large) return large;
        if (window.matchMedia('(min-width: 640px)').matches && desktop) return desktop;
        return mobile || desktop || large;
      }

      load() {
        if (!this.video.getAttribute('src')) {
          const src = this.source();
          if (!src) return;
          this.video.setAttribute('src', src);
          this.video.load();
        }
      }

      setQuietVolume() {
        this.video.volume = window.matchMedia('(min-width: 760px)').matches ? 0.72 : 0.18;
      }

      restartOnFirstUnmute() {
        if (!this.restartOnUnmute || this.restartedOnUnmute || !this.video.muted) return;
        this.restartedOnUnmute = true;
        try {
          this.video.currentTime = 0;
        } catch (e) {
          this.pendingSeek = 0;
        }
        this.updateProgress();
      }

      updateSound() {
        if (!this.sound) return;
        const on = !this.video.muted;
        this.sound.setAttribute('aria-pressed', on ? 'true' : 'false');
        this.sound.setAttribute('aria-label', on ? this.dataset.labelSoundOff : this.dataset.labelSoundOn);
      }

      showVideo() {
        this.classList.add('is-playing');
        if (this.center) this.center.hidden = true;
        this.updateSound();
      }

      play(withSound) {
        this.manuallyPaused = false;
        this.load();
        this.setQuietVolume();
        if (withSound) this.restartOnFirstUnmute();
        this.video.muted = !withSound;
        this.updateSound();
        const attempt = this.video.play();
        if (attempt && typeof attempt.catch === 'function') {
          attempt.then(() => this.showVideo()).catch(() => {
            // sound blocked: fall back to muted playback, as live
            this.video.muted = true;
            this.updateSound();
            this.video
              .play()
              .then(() => this.showVideo())
              .catch(() => {
                this.classList.remove('is-playing');
              });
          });
        }
      }

      pause(showButton) {
        this.video.pause();
        if (showButton && this.center && this.classList.contains('is-playing')) this.center.hidden = false;
      }

      updateProgress() {
        if (!this.progress || this.seeking) return;
        const duration = this.video.duration || 0;
        const ratio = duration ? Math.min(1, Math.max(0, (this.video.currentTime || 0) / duration)) : 0;
        this.setProgress(ratio);
      }

      setProgress(ratio) {
        this.progress.value = Math.round(ratio * 1000);
        this.progress.style.setProperty('--fill', `${ratio * 100}%`);
      }

      seekTo(ratio) {
        this.load();
        const safe = Math.min(1, Math.max(0, ratio || 0));
        if (this.progress) this.setProgress(safe);
        if (Number.isFinite(this.video.duration) && this.video.duration > 0) {
          this.video.currentTime = this.video.duration * safe;
          this.pendingSeek = null;
        } else {
          this.pendingSeek = safe;
        }
      }

      seekBy(seconds) {
        this.load();
        const duration = Number.isFinite(this.video.duration) && this.video.duration > 0 ? this.video.duration : 0;
        let next = Math.max(0, (this.video.currentTime || 0) + seconds);
        if (duration) next = Math.min(duration, next);
        try {
          this.video.currentTime = next;
        } catch (e) {
          /* not seekable yet */
        }
        this.updateProgress();
      }
    }
  );
}
